import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { isClientTier } from "../lib/roles";
import { ActionButton, FormField, inputClass, PillBadge } from "../components/Primitives";
import { ShieldCheck, AlertOctagon } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/auth/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(errorMessage(e, "Invite not found")));
  }, [token]);

  const submit = async () => {
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      const res = await api.post(`/auth/invite/${token}/accept`, { password });
      // Cookies are set by the backend; hydrate the auth context from the body.
      if (setUser && res.data?.user) setUser(res.data.user);
      toast.success("Account activated");
      nav(isClientTier(res.data?.user) ? "/portal" : "/", { replace: true });
    } catch (e) {
      toast.error(errorMessage(e, "Failed to activate"));
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-8">
        <div className="max-w-md w-full border border-[#27272A] bg-[#121212] p-10 text-center" data-testid="invite-error">
          <AlertOctagon className="size-10 text-[#FF3B30] mx-auto mb-4" />
          <div className="label-mute mb-2">Invite Status</div>
          <div className="font-display text-2xl uppercase font-bold tracking-tight mb-3">Link Invalid</div>
          <div className="text-sm text-zinc-400 mb-6">{error}</div>
          <button onClick={() => nav("/login")} className="text-xs uppercase tracking-widest text-[#FF3B30] hover:text-white">
            Return to login →
          </button>
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-zinc-500 flex items-center justify-center font-mono text-xs uppercase tracking-widest">
        Verifying invite…
      </div>
    );
  }

  const tone = ({ admin: "red", supervisor: "blue", client: "amber" })[invite.role] || "default";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-8">
      <div className="max-w-md w-full border border-[#27272A] bg-[#121212]" data-testid="accept-invite-form">
        <div className="border-b border-[#27272A] px-8 py-6">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="size-6 text-[#FF3B30]" />
            <div className="label-mute">Iris Security · Account Activation</div>
          </div>
          <div className="font-display text-2xl uppercase font-bold tracking-tight">Welcome, {invite.name}</div>
          <div className="text-sm text-zinc-400 mt-1">
            {invite.email} <span className="mx-1 text-zinc-600">·</span> <PillBadge tone={tone}>{invite.role}</PillBadge>
          </div>
        </div>
        <div className="p-8 space-y-5">
          <p className="text-xs text-zinc-500 leading-relaxed">
            Set a password to activate your operator account. Minimum 8 characters.
          </p>
          <FormField label="Password">
            <input
              type="password"
              data-testid="invite-password-input"
              className={inputClass}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
          </FormField>
          <FormField label="Confirm Password">
            <input
              type="password"
              data-testid="invite-confirm-input"
              className={inputClass}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
          </FormField>
          <ActionButton onClick={submit} disabled={submitting} data-testid="accept-invite-button">
            {submitting ? "Activating…" : "Activate Account"}
          </ActionButton>
        </div>
      </div>
    </div>
  );
}
